import baseApi from 'shared/api/baseApi';

import type {
  Webhook,
  WebhookCreateRequest,
  WebhookLimitInfo,
  WebhookListResponse,
  WebhookTestResult,
  WebhookUpdateRequest,
} from '../types/webhook';

interface WebhookListParams {
  projectId?: number;
  subProjectId?: number | null;
  enabled?: boolean;
  page?: number;
  limit?: number;
}

const webhooksApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getPartnerWebhooks: build.query<WebhookListResponse, WebhookListParams>({
      query: ({ projectId, subProjectId, enabled, page = 1, limit = 20 }) => {
        const params = new URLSearchParams();
        if (projectId) params.append('projectId', projectId.toString());
        if (subProjectId !== undefined) {
          params.append('subProjectId', subProjectId === null ? 'null' : subProjectId.toString());
        }
        if (enabled !== undefined) params.append('enabled', enabled.toString());
        params.append('page', page.toString());
        params.append('limit', limit.toString());

        return {
          url: `/partner/webhooks?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'PartnerWebhooks' as const, id })),
              { type: 'PartnerWebhooks', id: 'LIST' },
            ]
          : [{ type: 'PartnerWebhooks', id: 'LIST' }],
    }),

    getWebhookLimits: build.query<WebhookLimitInfo, { projectId?: number; subProjectId?: number | null }>({
      query: ({ projectId, subProjectId }) => {
        const params = new URLSearchParams();
        if (projectId) params.append('projectId', projectId.toString());
        if (typeof subProjectId === 'number') {
          params.append('subProjectId', subProjectId.toString());
        }

        return {
          url: `/partner/webhooks/limits?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { projectId, subProjectId }) => [
        { type: 'PartnerWebhooks', id: `LIMITS-${projectId ?? 'all'}-${subProjectId ?? 'general'}` },
      ],
    }),

    createPartnerWebhook: build.mutation<Webhook, WebhookCreateRequest & { projectId?: number }>({
      query: ({ projectId, ...body }) => ({
        url: '/partner/webhooks',
        method: 'POST',
        body,
        params: projectId !== undefined ? { projectId } : undefined,
      }),
      invalidatesTags: (result, error, { projectId, subProjectId }) => [
        { type: 'PartnerWebhooks', id: 'LIST' },
        { type: 'PartnerWebhooks', id: `LIMITS-${projectId ?? 'all'}-${subProjectId ?? 'general'}` },
        { type: 'PartnerWebhooks', id: `LIMITS-${projectId ?? 'all'}-general` },
      ],
    }),

    updatePartnerWebhook: build.mutation<Webhook, { webhookId: number; projectId?: number } & WebhookUpdateRequest>({
      query: ({ webhookId, projectId, ...body }) => ({
        url: `/partner/webhooks/${webhookId}`,
        method: 'PATCH',
        body,
        params: projectId !== undefined ? { projectId } : undefined,
      }),
      invalidatesTags: (result, error, { webhookId, projectId, subProjectId }) => [
        { type: 'PartnerWebhooks', id: webhookId },
        { type: 'PartnerWebhooks', id: 'LIST' },
        { type: 'PartnerWebhooks', id: `LIMITS-${projectId ?? 'all'}-${subProjectId ?? 'general'}` },
        { type: 'PartnerWebhooks', id: `LIMITS-${projectId ?? 'all'}-general` },
      ],
    }),

    deletePartnerWebhook: build.mutation<{ message: string; id: number }, { webhookId: number; projectId?: number }>({
      query: ({ webhookId, projectId }) => ({
        url: `/partner/webhooks/${webhookId}`,
        method: 'DELETE',
        params: projectId !== undefined ? { projectId } : undefined,
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'PartnerWebhooks', id: 'LIST' },
        { type: 'PartnerWebhooks', id: `LIMITS-${projectId ?? 'all'}-general` },
      ],
    }),

    testPartnerWebhook: build.mutation<WebhookTestResult, { webhookId: number; projectId?: number }>({
      query: ({ webhookId, projectId }) => ({
        url: `/partner/webhooks/${webhookId}/test`,
        method: 'POST',
        params: projectId !== undefined ? { projectId } : undefined,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPartnerWebhooksQuery,
  useGetWebhookLimitsQuery,
  useCreatePartnerWebhookMutation,
  useUpdatePartnerWebhookMutation,
  useDeletePartnerWebhookMutation,
  useTestPartnerWebhookMutation,
} = webhooksApi;
